import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowDownUp, ArrowLeft, ArrowRight, BarChart3, CalendarDays, Camera, Check, CheckCircle2, Clock3,
  ChevronDown, ChevronLeft, ChevronRight, ChevronUp, CircleHelp, CircleUserRound, Copy, Database,
  Download, ExternalLink, Eye, EyeOff, FileText, Filter, Gauge, GripVertical, Heart, ImagePlus, LockKeyhole, LogOut,
  Mail, Menu, MoreHorizontal, Newspaper, Pencil, Plus, RefreshCcw, Search, ShieldCheck,
  Sparkles, Star, Target, Trash2, Trophy, Upload, UserRoundCog, Users, X,
} from 'lucide-react';
import CalendarPage from './CalendarPage.jsx';
import { ProtocolList, ProtocolDetail, HomeProtocols, AdminProtocols, ProtocolEditor } from './Protocols.jsx';
import Metrika from './Metrika.jsx';
import { applyDocumentSeo, SEO_ORIGIN, seoForPathname } from './seo.js';
import { movePlacement } from '../shared/placements.mjs';
import { recordNumberFromQuery } from '../shared/record-search.mjs';

function editableRecord(record) {
  return { ...record, placements: record.placements.map((place) => ({ ...place, editorKey: crypto.randomUUID() })) };
}

async function api(path, options = {}) {
  const isForm = options.body instanceof FormData;
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    ...options,
    headers: isForm ? options.headers : { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || 'Не удалось выполнить запрос');
  }
  return response.status === 204 ? null : response.json();
}

const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);
const formatDate = (value, options = {}) => value ? new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', ...options }).format(new Date(value)) : '—';
const statusLabel = { active: 'действующий', veteran: 'ветеран', deceased: 'ушёл из жизни' };
const CONTACT_EMAIL = 'ovistats@gmail.com';
const SUPPORT_CARD = '2202 2005 4203 6908';
const ADMIN_DRAWER_DEFAULT_WIDTH = 408;
const ADMIN_DRAWER_MIN_WIDTH = 340;
const ADMIN_DRAWER_MAX_WIDTH = 680;
const clampAdminDrawerWidth = (width) => Math.min(ADMIN_DRAWER_MAX_WIDTH, Math.max(ADMIN_DRAWER_MIN_WIDTH, Math.round(width)));
const routeTransliteration = {
  а:'a', б:'b', в:'v', г:'g', д:'d', е:'e', ё:'e', ж:'zh', з:'z', и:'i', й:'y', к:'k', л:'l', м:'m', н:'n', о:'o', п:'p',
  р:'r', с:'s', т:'t', у:'u', ф:'f', х:'h', ц:'ts', ч:'ch', ш:'sh', щ:'sch', ъ:'', ы:'y', ь:'', э:'e', ю:'yu', я:'ya',
};
function toRouteSlug(value) {
  return [...String(value || '').toLowerCase()].map((letter) => routeTransliteration[letter] ?? letter).join('')
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 150);
}

function GalaxySymbol({ galaxy }) {
  return galaxy.illustration
    ? <img className="galaxy-symbol" src={galaxy.illustration} alt="" width="80" height="80" loading="lazy" decoding="async" />
    : <span className="reference-galaxy-number-symbol" aria-hidden="true">{String(galaxy.id).padStart(2, '0')}</span>;
}

function recordCountLabel(value) {
  const number = Number(value) || 0;
  const tens = number % 100;
  const units = number % 10;
  const noun = tens >= 11 && tens <= 14 ? 'рекордов' : units === 1 ? 'рекорд' : units >= 2 && units <= 4 ? 'рекорда' : 'рекордов';
  return `${number.toLocaleString('ru-RU')} ${noun}`;
}

function galaxyCountLabel(value) {
  const number = Number(value) || 0;
  const tens = number % 100;
  const units = number % 10;
  const noun = tens >= 11 && tens <= 14 ? 'галактик' : units === 1 ? 'галактика' : units >= 2 && units <= 4 ? 'галактики' : 'галактик';
  return `${number} ${noun}`;
}

function App() {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);
  const { pathname } = useLocation();
  const needsAuth = pathname.startsWith('/admin') || pathname === '/login' || pathname === '/account';
  useEffect(() => {
    if (!needsAuth) { setBooting(false); return undefined; }
    let active = true;
    setBooting(true);
    api('/auth/me').then(({ user: value }) => { if (active) setUser(value); })
      .catch(() => { if (active) setUser(null); })
      .finally(() => { if (active) setBooting(false); });
    return () => { active = false; };
  }, [needsAuth]);
  const auth = useMemo(() => ({ user, setUser, booting }), [user, booting]);
  return (
    <AuthContext.Provider value={auth}>
      <div className="site-shell">
        <SeoManager />
        <Metrika />
        <Header />
        <main id="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/records" element={<Records />} />
            <Route path="/records/:id" element={<RecordDetail />} />
            <Route path="/protocols" element={<ProtocolList request={api} />} />
            <Route path="/protocols/:id" element={<ProtocolDetail request={api} />} />
            <Route path="/preview/protocols/*" element={<Navigate to="/protocols" replace />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsArticle />} />
            <Route path="/predictions" element={<Predictions />} />
            <Route path="/calendar" element={<CalendarPage request={api} />} />
            <Route path="/support" element={<Support />} />
            <Route path="/pro" element={<Navigate to="/support" replace />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/register" element={<Navigate to="/records" replace />} />
            <Route path="/account" element={<EditorOnly><Account /></EditorOnly>} />
            <Route path="/admin/*" element={<EditorOnly><Admin /></EditorOnly>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </AuthContext.Provider>
  );
}

function SeoManager() {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  useEffect(() => { applyDocumentSeo(seoForPathname(location.pathname + location.search)); }, [location.pathname, location.search]);
  return null;
}

function Header() {
  const [open, setOpen] = useState(false);
  const { user, setUser } = useAuth();
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  async function logout() { await api('/auth/logout', { method: 'POST' }); setUser(null); }
  const isHome = location.pathname === '/';
  const isRecords = location.pathname === '/records';
  const isCalendar = location.pathname === '/calendar';
  if (location.pathname.startsWith('/admin')) return null;
  return (
    <header className={`site-header reference-public-header${isHome ? ' is-home' : ''}${isRecords ? ' is-records' : ''}${isCalendar ? ' is-calendar' : ''}${user ? ' has-user' : ''}`}>
      <a href="#main-content" className="skip-link">К содержанию</a>
      <div className="container nav-wrap">
        <Link to="/" className={`reference-brand${isHome ? ' is-home' : ''}${isRecords ? ' is-compact' : ''}`} aria-label="OVI Stats, главная">
          {isHome ? <><span>OVI STATS</span><b>8</b></> : <><b>8</b><span>OVI STATS</span></>}
        </Link>
        <button className="menu-button" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Открыть меню">{open ? <X /> : <Menu />}</button>
        <nav className={open ? 'main-nav reference-nav is-open' : 'main-nav reference-nav'} aria-label="Основное меню">
          <NavLink to="/" end>Главная</NavLink>
          <NavLink to="/records">Рекорды</NavLink>
          <NavLink to="/protocols">Протоколы</NavLink>
          <NavLink to="/news">Новости</NavLink>
          <NavLink to="/predictions">Прогнозы</NavLink>
          <NavLink to="/calendar">Календарь</NavLink>
        </nav>
        <div className="nav-actions reference-actions">
          {!isRecords && user && (
            <>
              {['editor', 'admin'].includes(user.role) && <Link className="icon-link" to="/admin" title="Админка"><Gauge /></Link>}
              <Link className="account-link" to="/account"><CircleUserRound /> <span>{user.displayName}</span></Link>
              <button className="icon-button" onClick={logout} title="Выйти"><LogOut /></button>
            </>
          )}
          <Link className="reference-pro-button" to="/support" aria-label="Поддержать проект">Поддержать<span className="support-label-extra"> проект</span></Link>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div className="footer-about">
          <Link className="footer-reference-brand" to="/" aria-label="OVI Stats, главная"><b>8</b><span>OVI STATS</span></Link>
          <p>База рекордов Александра Овечкина: голы в равных составах, большинстве и меньшинстве, тройки лучших игроков и обновления статистики. Все рекорды, новости, прогнозы и календарь открыты бесплатно и без регистрации.</p>
        </div>
        <nav className="footer-links" aria-label="Навигация в подвале">
          <h2>Разделы</h2>
          <Link to="/">Главная</Link>
          <Link to="/records">Рекорды</Link>
          <Link to="/protocols">Протоколы</Link>
          <Link to="/news">Новости</Link>
          <Link to="/predictions">Прогнозы</Link>
          <Link to="/calendar">Календарь</Link>
        </nav>
        <div className="footer-principles">
          <h2>О проекте</h2>
          <p>Независимый фанатский ресурс, не связанный с NHL или клубом Washington Capitals.</p>
          <Link to="/support">Поддержать проект</Link>
          <div className="footer-contact">
            <p>Вопросы, предложения и сотрудничество</p>
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </div>
        </div>
      </div>
      <div className="container footer-bottom"><span>© {new Date().getFullYear()} OVI Stats</span><span>Фанатский проект · Неофициальный ресурс</span><Link to="/login">Вход для редакции</Link></div>
    </footer>
  );
}

function Home() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [showAllGalaxies, setShowAllGalaxies] = useState(false);
  const [galaxyRange, setGalaxyRange] = useState({ start: 1, end: 4, atStart: true, atEnd: false });
  const galaxyRail = useRef(null);
  const navigate = useNavigate();
  useEffect(() => { api('/bootstrap').then(setData).catch((e) => setError(e.message)); }, []);
  useEffect(() => {
    const rail = galaxyRail.current;
    if (!rail || !data?.galaxies?.length) return undefined;
    let frame = 0;
    function syncGalaxyRange() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const cards = [...rail.querySelectorAll('.reference-galaxy-card')];
        const railRect = rail.getBoundingClientRect();
        const visibleIndexes = cards.flatMap((card, index) => {
          const cardRect = card.getBoundingClientRect();
          return cardRect.right > railRect.left + 2 && cardRect.left < railRect.right - 2 ? [index] : [];
        });
        const next = {
          start: (visibleIndexes[0] ?? 0) + 1,
          end: (visibleIndexes.at(-1) ?? 0) + 1,
          atStart: rail.scrollLeft <= 2,
          atEnd: rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2,
        };
        setGalaxyRange((current) => Object.keys(next).every((key) => current[key] === next[key]) ? current : next);
      });
    }
    syncGalaxyRange();
    rail.addEventListener('scroll', syncGalaxyRange, { passive: true });
    const resizeObserver = new ResizeObserver(syncGalaxyRange);
    resizeObserver.observe(rail);
    return () => {
      cancelAnimationFrame(frame);
      rail.removeEventListener('scroll', syncGalaxyRange);
      resizeObserver.disconnect();
    };
  }, [data]);
  if (error) return <ErrorState message={error} />;
  if (!data) return <Loading />;
  const lead = data.lead;
  const leadValue = lead.placements?.[0]?.value ?? 929;
  const editorialItems = data.news.map((item) => ({
    ...item,
    imageUrl: item.imageUrl || '/media/server/ovechkin-hero.jpg',
    to: `/news/${item.slug}`,
  }));
  const newsStamp = (value) => {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return 'Дата не указана';
    const day = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(date).replace(' г.', '');
    const time = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(date);
    return `${day}, ${time}`;
  };
  function submitSearch(event) {
    event.preventDefault();
    navigate(query.trim() ? `/records?q=${encodeURIComponent(query.trim())}` : '/records');
  }
  function scrollGalaxies(direction) {
    const rail = galaxyRail.current;
    rail?.scrollBy({ left: direction * rail.clientWidth, behavior: 'smooth' });
  }
  return (
    <div className="home-reference">
      <section className="reference-hero">
        <div className="reference-rink" aria-hidden="true" />
        <div className="reference-trajectory" aria-hidden="true">
          <svg viewBox="0 0 1000 300" preserveAspectRatio="none">
            <polyline points="0,258 82,245 145,225 205,202 259,164 302,135 347,118 408,116 470,110 526,92 578,67 620,42 648,50 720,46 775,42 828,28 882,18 930,6 1000,-15" />
            {[['82','245'],['145','225'],['205','202'],['259','164'],['302','135'],['347','118'],['470','110'],['526','92'],['578','67'],['620','42'],['648','50'],['775','42'],['828','28'],['930','6']].map(([cx, cy]) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="4" />)}
          </svg>
        </div>
        <div className="reference-hero-photo" aria-hidden="true"><img src="/media/server/ovechkin-hero.jpg" alt="" /></div>
        <div className="reference-hero-copy">
          <h1>{recordCountLabel(data.stats.records)}.<br />Одна большая история.</h1>
          <p>Все достижения Александра Овечкина —<br />от первого гола до рекорда вечности.</p>
          <div className="reference-hero-actions">
            <Link className="reference-primary" to="/records">Смотреть рекорды</Link>
            <a className="reference-secondary" href="#galaxies">Как устроены галактики</a>
          </div>
        </div>
        <Link to={`/records/${lead.id}`} className="reference-record-card" aria-label={`Открыть рекорд: ${leadValue} голов`}>
          <strong>{leadValue}</strong>
          <span>голов в регулярных<br />чемпионатах</span>
        </Link>
      </section>

      <section className={`reference-galaxies${showAllGalaxies ? ' is-expanded' : ''}`} id="galaxies">
        <div className="reference-section-toolbar">
          <h2>Галактики рекордов</h2>
          <span className="reference-heading-line" />
          <form className="reference-inline-search" onSubmit={submitSearch}>
            <button type="submit" className="reference-search-submit" aria-label="Искать"><Search aria-hidden="true" /></button>
            <label className="sr-only" htmlFor="home-record-search">Поиск по рекордам</label>
            <input id="home-record-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Поиск по рекордам" />
          </form>
          <button className="reference-all-galaxies" type="button" onClick={() => setShowAllGalaxies((value) => !value)} aria-expanded={showAllGalaxies} aria-controls="home-all-galaxies">
            <span>{galaxyRange.start}–{galaxyRange.end} из {data.galaxies.length}</span>
            <b>{showAllGalaxies ? 'Свернуть' : 'Все галактики'}</b>
            <ChevronDown aria-hidden="true" />
          </button>
        </div>
        <button className="reference-rail-arrow previous" type="button" onClick={() => scrollGalaxies(-1)} aria-label="Предыдущие галактики" disabled={galaxyRange.atStart}><ArrowLeft /></button>
        <div className="reference-galaxy-rail" ref={galaxyRail}>
          {data.galaxies.map((galaxy) => (
            <Link key={galaxy.id} to={`/records?galaxy=${galaxy.slug}`} className="reference-galaxy-card">
              <GalaxySymbol galaxy={galaxy} />
              <div><h3>{galaxy.name}</h3><b>{recordCountLabel(galaxy.recordCount)}</b><p>{galaxy.description || `Рекорды ${galaxy.recordRange}`}</p></div>
              <ChevronRight aria-hidden="true" />
            </Link>
          ))}
        </div>
        <button className="reference-rail-arrow next" type="button" onClick={() => scrollGalaxies(1)} aria-label="Следующие галактики" disabled={galaxyRange.atEnd}><ArrowRight /></button>
        <div className="reference-all-galaxies-panel" id="home-all-galaxies" hidden={!showAllGalaxies}>
          <div className="reference-all-galaxies-heading">
            <div><span>Полный каталог</span><h3>{galaxyCountLabel(data.galaxies.length)}</h3></div>
            <button type="button" onClick={() => setShowAllGalaxies(false)} aria-label="Закрыть полный список галактик"><X /></button>
          </div>
          <div className="reference-all-galaxies-grid">
            {data.galaxies.map((galaxy) => (
              <Link key={galaxy.id} to={`/records?galaxy=${galaxy.slug}`}>
                <GalaxySymbol galaxy={galaxy} />
                <div className="all-galaxies-copy"><small><b>{String(galaxy.id).padStart(2, '0')}</b> · Рекорды {galaxy.recordRange}</small><strong>{galaxy.name}</strong><p>{galaxy.description}</p></div>
                <ChevronRight aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="reference-news">
        <div className="reference-news-heading"><h2>Последние новости</h2><span /><Link to="/news">Смотреть все новости <ChevronRight /></Link></div>
        <div className="reference-news-rail">
          {editorialItems.map((item) => (
            <Link to={item.to} className="reference-news-item" key={item.id}>
              <img src={item.imageUrl} alt="" width="105" height="96" loading="lazy" decoding="async" />
              <div><time>{newsStamp(item.publishedAt)}</time><p>{item.title}</p></div>
              <ArrowRight aria-hidden="true" />
            </Link>
          ))}
        </div>
      </section>

      <HomeProtocols items={data.protocols} />
      <section className="reference-pro-strip">
        <div className="reference-pro-intro"><Heart /><div><h2>OVI STATS <em>ДЛЯ ВСЕХ</em></h2><p>Вся статистика открыта.<br />Без подписки и регистрации.</p></div></div>
        <Link className="reference-pro-feature" to="/records"><Database /><div><b>Полные рекорды</b><span>Все игроки и значения<br />во всех галактиках.</span></div></Link>
        <Link className="reference-pro-feature" to="/predictions"><Target /><div><b>Прогнозы и разборы</b><span>Редакционные оценки<br />с обоснованием.</span></div></Link>
        <Link className="reference-pro-feature" to="/calendar"><CalendarDays /><div><b>Весь календарь</b><span>Матчи лиги и фильтры<br />по всем командам.</span></div></Link>
        <Link className="reference-pro-feature" to="/support"><Heart /><div><b>Поддержать проект</b><span>По желанию.<br />Все материалы доступны каждому.</span></div></Link>
      </section>
    </div>
  );
}

function SectionHead({ kicker, title, link, linkText }) {
  return <div className="section-head"><div><div className="eyebrow muted">{kicker}</div><h2>{title}</h2></div>{link && <Link className="text-link dark" to={link}>{linkText}<ArrowRight /></Link>}</div>;
}

function NewsCard({ item, featured }) {
  return <article className={featured ? 'news-card featured' : 'news-card'}>
    <div className="news-image" style={{ backgroundImage: `url(${item.imageUrl || '/media/legacy-hero.jpg'})` }}><span className="tag">Новости</span></div>
    <div className="news-copy"><time>{formatDate(item.publishedAt)}</time><h3>{item.title}</h3><p>{item.excerpt}</p><Link to="/news">Читать материал <ArrowRight /></Link></div>
  </article>;
}

function MatchRow({ match }) {
  const date = new Date(match.startsAt);
  return <div className="match-row"><div className="match-date"><b>{date.getDate()}</b><span>{date.toLocaleString('ru-RU', { month: 'short' })}</span></div><div><strong>Вашингтон — {match.opponent}</strong><p>{match.venue} · {date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</p></div><CalendarDays /></div>;
}

function Records() {
  const [params, setParams] = useSearchParams();
  const [bootstrap, setBootstrap] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRecordId, setExpandedRecordId] = useState(null);
  const galaxySidebarList = useRef(null);
  const q = params.get('q') || '';
  const [draftQ, setDraftQ] = useState(q);
  const galaxy = params.has('galaxy') ? params.get('galaxy') : (q ? '' : 'strelets');
  const pageSize = Number(params.get('pageSize') || 20);
  const requestParams = new URLSearchParams(params);
  if (galaxy) requestParams.set('galaxy', galaxy);
  requestParams.set('pageSize', String(pageSize));
  const paramsKey = requestParams.toString();
  useEffect(() => { api('/bootstrap').then(setBootstrap).catch((e) => setError(e.message)); }, []);
  useEffect(() => { setDraftQ(q); }, [q]);
  useEffect(() => {
    setExpandedRecordId(null);
    setLoading(true);
    setError('');
    api(`/records?${paramsKey}`).then(setData).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [paramsKey]);
  useEffect(() => {
    const list = galaxySidebarList.current;
    const active = list?.querySelector('button.active');
    if (!list || !active) return;
    const itemTop = active.offsetTop;
    const itemBottom = itemTop + active.offsetHeight;
    if (itemTop < list.scrollTop) list.scrollTo({ top: itemTop, behavior: 'smooth' });
    else if (itemBottom > list.scrollTop + list.clientHeight) list.scrollTo({ top: itemBottom - list.clientHeight, behavior: 'smooth' });
  }, [galaxy, bootstrap]);
  function update(key, value) { const next = new URLSearchParams(params); value || key === 'galaxy' ? next.set(key, value) : next.delete(key); if (key !== 'page') next.delete('page'); setParams(next); }
  function submitSearch(event) {
    event.preventDefault();
    const query = draftQ.trim();
    const next = new URLSearchParams(params);
    query ? next.set('q', query) : next.delete('q');
    next.delete('page');
    if (recordNumberFromQuery(query) !== null) {
      next.set('galaxy', '');
      next.delete('stage');
      next.delete('position');
    }
    setParams(next);
  }
  function resetFilters() {
    const next = new URLSearchParams();
    const firstGalaxy = bootstrap?.galaxies[0]?.slug;
    if (firstGalaxy) next.set('galaxy', firstGalaxy);
    next.set('pageSize', String(pageSize));
    setDraftQ('');
    setParams(next);
  }
  const currentGalaxy = bootstrap?.galaxies.find((item) => item.slug === galaxy);
  const globalTotal = bootstrap?.stats.records ?? null;
  const from = data?.total ? (data.page - 1) * data.pageSize + 1 : 0;
  const to = data?.total ? Math.min(data.page * data.pageSize, data.total) : 0;
  return (
    <div className="records-reference">
      <aside className="records-reference-sidebar" aria-label="Галактики рекордов">
        <div className="records-sidebar-title">Галактики <ChevronDown aria-hidden="true" /></div>
        <div className="records-sidebar-list" ref={galaxySidebarList} tabIndex="0" aria-label="Все галактики">
          {bootstrap?.galaxies.map((item) => (
            <button key={item.id} className={galaxy === item.slug ? 'active' : ''} aria-current={galaxy === item.slug ? 'true' : undefined} title={item.description} onClick={() => update('galaxy', item.slug)}>
              <span className="records-sidebar-number">{String(item.id).padStart(2, '0')}</span>
              <strong>{item.name}</strong>
              <small>{item.recordRange.split(', ').map((range, index) => <React.Fragment key={range}>{index > 0 && <br />}{range}</React.Fragment>)}</small>
            </button>
          ))}
        </div>
      </aside>
      <section className="records-reference-main">
        <div className="records-reference-heading">
          <h1>Рекорды Александра Овечкина</h1>
          <div className="records-reference-search-row">
            <form className="records-reference-search" onSubmit={submitSearch}>
              <button type="submit" aria-label="Найти"><Search aria-hidden="true" /></button>
              <label className="sr-only" htmlFor="records-search">Найти рекорд, игрока или показатель</label>
              <input id="records-search" value={draftQ} onChange={(event) => setDraftQ(event.target.value)} placeholder="Найти рекорд, игрока или показатель" />
            </form>
            <p><b>{globalTotal == null ? 'Загрузка каталога' : recordCountLabel(globalTotal)}</b> · <b>{bootstrap ? galaxyCountLabel(bootstrap.galaxies.length) : '—'}</b></p>
          </div>
        </div>
        <div className="records-reference-filters">
          <label><span>Галактика</span><select value={galaxy} onChange={(event) => update('galaxy', event.target.value)}><option value="">Все галактики</option>{bootstrap?.galaxies.map((item) => <option key={item.id} value={item.slug}>{String(item.id).padStart(2, '0')} {item.name}</option>)}</select></label>
          <label><span>Этап</span><select value={params.get('stage') || ''} onChange={(event) => update('stage', event.target.value)}><option value="">Все этапы</option><option value="РЧ">РЧ</option><option value="КС">КС</option><option value="РЧ+КС">РЧ+КС</option></select></label>
          <label><span>Положение Овечкина</span><select value={params.get('position') || ''} onChange={(event) => update('position', event.target.value)}><option value="">Любое</option><option value="leader">Лидер</option><option value="other">Не первое место</option></select></label>
          <button className="records-reference-reset" onClick={resetFilters}><RefreshCcw aria-hidden="true" /> Сбросить фильтры</button>
        </div>
        <div className="records-reference-title-row">
          <div className={`records-galaxy-heading${currentGalaxy ? ' has-galaxy' : ''}`}>
            {currentGalaxy && <GalaxySymbol galaxy={currentGalaxy} />}
            <div><h2>{currentGalaxy?.name || 'Все рекорды'}</h2>{currentGalaxy?.description && <p>{currentGalaxy.description}</p>}</div>
          </div>
          <span>Сортировка: <b>№</b> <ArrowDownUp aria-hidden="true" /></span>
        </div>
        <div className="records-reference-table-head" aria-hidden="true"><span>№</span><span>Рекорд / показатель <ArrowDownUp /></span><span>Этап</span><span>1 место <CircleHelp /></span><span /></div>
        {error ? <ErrorState message={error} /> : loading ? <Loading /> : data?.items.length ? (
          <div className="records-reference-list">{data.items.map((record) => <ReferenceRecordRow key={record.id} record={record} expanded={expandedRecordId === record.id} onToggle={() => setExpandedRecordId((current) => current === record.id ? null : record.id)} />)}</div>
        ) : <EmptyState title="Ничего не найдено" text="Измените запрос или сбросьте фильтры." />}
        {data && <ReferencePagination page={data.page} pages={data.pages} from={from} to={to} total={data.total} pageSize={data.pageSize} onPage={(value) => update('page', String(value))} onPageSize={(value) => update('pageSize', String(value))} />}
      </section>
    </div>
  );
}

function ReferenceRecordRow({ record, expanded, onToggle }) {
  const places = Array.from({ length: 3 }, (_, index) => record.placements?.find((place) => place.rank === index + 1));
  const leader = places[0];
  const leaderIsOvechkin = /овечкин/i.test(leader?.playerName || '');
  return <article className={`records-reference-record${expanded ? ' is-expanded' : ''}`}>
    <button className="records-reference-row records-reference-summary" type="button" aria-expanded={expanded} aria-controls={`record-placements-${record.id}`} aria-label={`${expanded ? 'Свернуть' : 'Раскрыть'} рекорд № ${record.id}: ${record.name}`} onClick={onToggle}>
      <span className="records-reference-number">{record.id}</span>
      <span className="records-reference-meta"><strong>{record.name}</strong><span>{record.title}</span></span>
      <span className="records-reference-stage">{record.stage}</span>
      <span className={`records-reference-leader${leaderIsOvechkin ? ' is-ovechkin' : ''}`}>
        <span className="records-reference-rank">1</span>
        <ReferencePlayerPhoto place={leader} />
        <span className="records-reference-player-copy"><strong>{leader?.playerName || '—'}</strong><small>{leader?.emptyState ? '' : statusLabel[leader?.playerStatus] || leader?.playerStatus || 'Статус не указан'}</small></span>
        <b>{formatRecordValue(leader?.value)}</b>
      </span>
      <span className="records-reference-disclosure"><ChevronDown aria-hidden="true" /></span>
    </button>
    {expanded && <div id={`record-placements-${record.id}`} className="records-reference-expanded" role="region" aria-label={`Второе и третье места рекорда № ${record.id}`}>
      <span className="records-reference-expanded-label"><b>Остальные места</b></span>
      <div className="records-reference-expanded-places">
        {places.slice(1).map((place, index) => {
          const rank = index + 2;
          const isOvechkin = /овечкин/i.test(place?.playerName || '');
          return <div key={place?.rank || rank} className={`records-reference-expanded-place${isOvechkin ? ' is-ovechkin' : ''}`}>
            <span className="records-reference-rank">{rank}</span>
            <ReferencePlayerPhoto place={place} />
            <span className="records-reference-player-copy"><strong>{place?.playerName || '—'}</strong><small>{place?.emptyState ? '' : statusLabel[place?.playerStatus] || place?.playerStatus || 'Статус не указан'}</small></span>
            <b>{formatRecordValue(place?.value)}</b>
          </div>;
        })}
      </div>
    </div>}
  </article>;
}

function formatRecordValue(value) { return value == null ? '—' : Number.isFinite(Number(value)) ? Number(value).toLocaleString('ru-RU') : value; }
function ReferencePlayerPhoto({ place }) {
  return place?.photoUrl
    ? <img className="records-reference-player-photo" src={place.photoUrl} alt="" width="48" height="48" loading="lazy" />
    : <span className="records-reference-player-photo placeholder" aria-hidden="true">{place?.emptyState ? '—' : place?.playerName?.slice(0, 1) || '—'}</span>;
}

function Pagination({ page, pages, onPage }) {
  if (pages <= 1) return null;
  return <nav className="pagination" aria-label="Страницы"><button disabled={page <= 1} onClick={() => onPage(page - 1)}><ArrowLeft /> Назад</button><span>Страница <b>{page}</b> из {pages}</span><button disabled={page >= pages} onClick={() => onPage(page + 1)}>Вперёд <ArrowRight /></button></nav>;
}

function ReferencePagination({ page, pages, from, to, total, pageSize, onPage, onPageSize }) {
  const candidates = [1, 2, 3, 4, 5, page - 1, page, page + 1, pages].filter((value) => value >= 1 && value <= Math.max(1, pages));
  const pageNumbers = [...new Set(candidates)].sort((a, b) => a - b);
  return <div className="records-reference-pagination">
    <span>Показано {from}–{to} из {total.toLocaleString('ru-RU')}</span>
    <nav aria-label="Страницы каталога">
      <button aria-label="Предыдущая страница" disabled={page <= 1} onClick={() => onPage(page - 1)}><ChevronLeft /></button>
      {pageNumbers.map((value, index) => <React.Fragment key={value}>{index > 0 && value - pageNumbers[index - 1] > 1 && <i>…</i>}<button className={value === page ? 'active' : ''} onClick={() => onPage(value)}>{value}</button></React.Fragment>)}
      <button aria-label="Следующая страница" disabled={page >= pages} onClick={() => onPage(page + 1)}><ChevronRight /></button>
    </nav>
    <label><span>На странице:</span><select value={pageSize} onChange={(event) => onPageSize(event.target.value)}><option value="20">20</option><option value="40">40</option><option value="60">60</option></select></label>
  </div>;
}

function RecordDetail() {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api(`/records/${id}`).then(setRecord).catch((e) => setError(e.message)); }, [id]);
  useEffect(() => {
    if (!record) return;
    const seo = seoForPathname(`/records/${id}`);
    applyDocumentSeo({
      ...seo,
      title: `${record.name.length > 48 ? `${record.name.slice(0, 47).trimEnd()}…` : record.name} | OVI Stats`,
      description: `Рекорд № ${record.id} «${record.name}»: ${record.title}. Этап ${record.stage}, галактика «${record.galaxyName}».`.slice(0, 158),
      h1: record.name,
      breadcrumb: record.name,
    });
  }, [id, record]);
  if (error) return <ErrorState message={error} />;
  if (!record) return <Loading />;
  return <div className="page record-detail-page"><div className="container narrow">
    <Link className="back-link" to={`/records?galaxy=${record.galaxySlug}`}><ArrowLeft /> К галактике «{record.galaxyName}»</Link>
    <div className="detail-head"><div><div className="eyebrow muted">Рекорд № {record.id} · {record.galaxyName}</div><h1>{record.name}</h1><p>{record.title} · {record.stage}</p></div></div>
    <div className="podium">{record.placements.map((place) => <article key={place.rank} className={`place place-${place.rank}`}><span className="rank">{place.rank}</span><PlayerAvatar player={place} /><div><h2>{place.playerName}</h2><p>{statusLabel[place.playerStatus] || place.playerStatus}</p></div><strong>{formatRecordValue(place.value)}</strong></article>)}</div>
  </div></div>;
}

function PlayerAvatar({ player }) { return player.photoUrl ? <img className="player-avatar" src={player.photoUrl} alt="" /> : <div className="player-avatar placeholder">{player.emptyState ? '—' : player.playerName?.slice(0, 1)}</div>; }
function News() {
  const [items, setItems] = useState(null);
  useEffect(() => { api('/news').then(setItems); }, []);
  return <PageIntro kicker="Редакция" title="Новости и разборы">
    {!items ? <Loading /> : <div className="article-list">{items.map((item) => <article key={item.id} className="article-card"><div className="article-image" style={{backgroundImage:`url(${item.imageUrl || '/media/legacy-hero.jpg'})`}} /><div><div className="article-meta"><span>Новости</span><time>{formatDate(item.publishedAt)}</time></div><h2><Link to={`/news/${item.slug}`}>{item.title}</Link></h2><p>{item.excerpt}</p><p className="article-body">{item.body}</p>{item.sourceUrl && <a href={item.sourceUrl} target="_blank" rel="noreferrer">Источник <ExternalLink /></a>}</div></article>)}</div>}
  </PageIntro>;
}

function NewsArticle() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api(`/news/${encodeURIComponent(slug)}`).then(setItem).catch((requestError) => setError(requestError.message)); }, [slug]);
  useEffect(() => {
    if (!item) return;
    const seo = seoForPathname(`/news/${slug}`);
    const title = item.title.length > 52 ? `${item.title.slice(0, 51).trimEnd()}…` : item.title;
    applyDocumentSeo({
      ...seo,
      title: `${title} | OVI Stats`,
      description: (item.excerpt || `Новости об Александре Овечкине: ${item.title}`).slice(0, 158),
      h1: item.title,
      image: item.imageUrl ? new URL(item.imageUrl, SEO_ORIGIN).href : seo.image,
      robots: seo.robots,
      type: 'article',
      breadcrumb: item.title,
      article: { headline: item.title, datePublished: item.publishedAt, dateModified: item.publishedAt },
    });
  }, [item, slug]);
  if (error) return <ErrorState message={error} />;
  if (!item) return <Loading />;
  return <PageIntro kicker="Новости" title={item.title} text={item.excerpt}>
    <article className="news-detail">
      {item.imageUrl && <img src={item.imageUrl} alt="" />}
      <div className="article-meta"><span>Новости</span><time>{formatDate(item.publishedAt)}</time></div>
      <p className="article-body">{item.body}</p>
      {item.sourceUrl && <a href={item.sourceUrl} target="_blank" rel="noreferrer">Источник <ExternalLink /></a>}
      <Link className="back-link" to="/news"><ArrowLeft /> Ко всем новостям</Link>
    </article>
  </PageIntro>;
}

function Predictions() {
  const [items, setItems] = useState(null);
  useEffect(() => { api('/predictions').then(setItems); }, []);
  return <PageIntro kicker="Редакционная аналитика" title="Прогнозы">
    {!items ? <Loading /> : items.length ? <div className="prediction-grid">{items.map((item) => <article className="prediction-card" key={item.id}><div className="prediction-top"><Target /><span>{item.confidence}% уверенности</span></div><h2>{item.title}</h2><p>{item.summary}</p><div className="rationale"><b>Почему так</b>{item.rationale}</div><time>{formatDate(item.publishedAt)}</time></article>)}</div> : <EmptyState title="Прогнозов пока нет" />}
  </PageIntro>;
}

function PageIntro({ kicker, title, text, children }) { return <div className="page"><div className="page-hero"><div className="container"><div className="eyebrow">{kicker}</div><h1>{title}</h1>{text && <p>{text}</p>}</div></div><div className="container page-content">{children}</div></div>; }

function Support() {
  const [copyStatus, setCopyStatus] = useState('');
  const cardNumber = useRef(null);
  async function copyCard() {
    try {
      await navigator.clipboard.writeText(SUPPORT_CARD.replace(/\s/g, ''));
      setCopyStatus('copied');
    } catch {
      cardNumber.current?.focus();
      cardNumber.current?.select();
      setCopyStatus('error');
    }
  }
  return <div className="page pro-page support-page">
    <div className="pro-hero"><div className="container support-hero-layout">
      <div className="support-intro">
        <div className="pro-mark large"><Heart /><span>OVI</span><b>STATS</b></div>
        <h1>Поддержать OVI Stats</h1>
        <p>Помогите нам сохранять архив рекордов, сверять статистику и развивать проект.</p>
        <p>Все материалы открыты бесплатно и без регистрации.</p>
        <Link className="button button-light" to="/records">Смотреть все рекорды <ArrowRight /></Link>
      </div>
      <section className="support-donation" aria-labelledby="support-donation-title">
        <span className="support-bank">Сбер</span>
        <h2 id="support-donation-title">Перевод на карту</h2>
        <input className="support-card-number" ref={cardNumber} value={SUPPORT_CARD} readOnly aria-label="Номер карты Сбера" />
        <button className="button support-copy-button" type="button" onClick={copyCard}>
          {copyStatus === 'copied' ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          {copyStatus === 'copied' ? 'Номер скопирован' : 'Скопировать номер'}
        </button>
        <p className="support-copy-status" role="status">{copyStatus === 'copied' ? 'Номер карты скопирован.' : copyStatus === 'error' ? 'Скопируйте номер карты вручную.' : ''}</p>
        <p className="support-donation-note">Перевод добровольный. Сумма — на ваше усмотрение.</p>
      </section>
    </div></div>
    <div className="container pro-benefits">
      <div><Database /><h2>Открытый архив</h2><p>Полные таблицы, игроки и значения доступны каждому.</p></div>
      <div><Heart /><h2>Добровольно</h2><p>Вы сами решаете, поддерживать ли проект и на какую сумму.</p></div>
      <div><CheckCircle2 /><h2>Одинаковый доступ</h2><p>Все материалы доступны каждому независимо от поддержки.</p></div>
      <div><CalendarDays /><h2>Проект развивается</h2><p>Мы собираем статистику, сверяем рекорды и обновляем материалы для всех читателей.</p></div>
    </div>
    <section className="container support-contact" aria-labelledby="support-contact-title">
      <Mail aria-hidden="true" />
      <div>
        <h2 id="support-contact-title">Связаться с нами</h2>
        <p>По вопросам, предложениям и сотрудничеству</p>
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </div>
    </section>
  </div>;
}

function AuthPage() {
  const { user, setUser, booting } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  if (booting) return <Loading />;
  if (user) return <Navigate to="/admin" replace />;
  async function submit(event) {
    event.preventDefault(); setError(''); setSubmitting(true);
    try {
      const data = await api('/auth/login', { method: 'POST', body: JSON.stringify(form) });
      setUser(data.user); navigate('/admin');
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  }
  return <div className="auth-page"><div className="auth-panel">
    <Link to="/" className="brand"><span>OVI</span><b>STATS</b></Link>
    <div className="eyebrow muted">Редакционная панель</div><h1>Вход для редакции</h1>
    <p>Авторизация нужна только редакторам и администраторам. Все материалы сайта открыты без аккаунта.</p>
    <form onSubmit={submit}>
      <Field label="Email" type="email" value={form.email} onChange={(value) => setForm({ ...form, email: value })} autoComplete="username" />
      <Field label="Пароль" type="password" value={form.password} onChange={(value) => setForm({ ...form, password: value })} autoComplete="current-password" />
      {error && <div className="form-error" role="alert">{error}</div>}
      <button className="button" type="submit" disabled={submitting}>{submitting ? 'Входим…' : 'Войти'} <ArrowRight /></button>
    </form>
    <p className="auth-switch"><Link to="/records">Перейти к рекордам без входа</Link></p>
  </div><div className="auth-art"><div><Trophy /><span>OVI STATS</span><p>Все рекорды открыты для каждого</p></div></div></div>;
}

function Field({ label, type = 'text', value, onChange, hint, ...props }) { return <label className="field"><span>{label}</span><input type={type} value={value} onChange={(e) => onChange(e.target.value)} required {...props} />{hint && <small>{hint}</small>}</label>; }

function Account() {
  const { user, setUser } = useAuth();
  async function logout() { await api('/auth/logout', { method: 'POST' }); setUser(null); }
  return <div className="page account-page"><div className="container narrow">
    <div className="account-head"><div className="avatar-large">{user.displayName.slice(0, 1)}</div><div><div className="eyebrow muted">Профиль редакции</div><h1>{user.displayName}</h1><p>{user.email}</p></div></div>
    <section className="account-card"><h2>{user.role === 'admin' ? 'Администратор' : 'Редактор'}</h2><p>Вам доступно управление материалами сайта. Все опубликованные материалы открыты читателям бесплатно.</p><Link className="button" to="/admin">Открыть редакционную панель</Link></section>
    <button className="logout-link" onClick={logout}><LogOut /> Выйти из аккаунта</button>
  </div></div>;
}

function EditorOnly({ children }) { const { user, booting } = useAuth(); if (booting) return <Loading />; return user && ['editor','admin'].includes(user.role) ? children : <Navigate to="/login" replace />; }

function Admin() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const recordsMode = location.pathname === '/admin/records';
  return <div className="admin-shell">
    <AdminSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
    <div className="admin-workspace">
      <AdminTopbar onMenu={() => setSidebarOpen((value) => !value)} />
      <div className={`admin-content${recordsMode ? ' is-records' : ''}`}><Routes>
        <Route index element={<AdminOverview />} />
        <Route path="records" element={<AdminRecords />} />
        <Route path="protocols" element={<AdminProtocols request={api} />} />
        <Route path="protocols/new" element={<ProtocolEditor request={api} />} />
        <Route path="protocols/:id" element={<ProtocolEditor request={api} />} />
        <Route path="players" element={<AdminPlayers />} />
        <Route path="news" element={<ContentManager kind="news" title="Новости" />} />
        <Route path="predictions" element={<ContentManager kind="predictions" title="Прогнозы" />} />
        <Route path="matches" element={<AdminCalendar />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="media" element={<AdminPlayers title="Медиа" />} />
      </Routes></div>
    </div>
  </div>;
}

function AdminTopbar({ onMenu }) {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const profileRef = useRef(null);
  const profileButtonRef = useRef(null);
  const [query, setQuery] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  useEffect(() => {
    function focusSearch(event) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', focusSearch);
    return () => window.removeEventListener('keydown', focusSearch);
  }, []);
  useEffect(() => setProfileOpen(false), [location.pathname]);
  useEffect(() => {
    if (!profileOpen) return undefined;
    function closeOutside(event) {
      if (!profileRef.current?.contains(event.target)) setProfileOpen(false);
    }
    function closeOnEscape(event) {
      if (event.key !== 'Escape') return;
      setProfileOpen(false);
      profileButtonRef.current?.focus();
    }
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [profileOpen]);
  function submit(event) {
    event.preventDefault();
    navigate(query.trim() ? `/admin/records?adminq=${encodeURIComponent(query.trim())}` : '/admin/records');
  }
  async function logout() {
    setLogoutError('');
    try {
      await api('/auth/logout', { method: 'POST' });
      setUser(null);
      navigate('/login', { replace: true });
    } catch (error) {
      setLogoutError(error.message);
    }
  }
  const roleLabel = { admin: 'Администратор', editor: 'Редактор', user: 'Пользователь' }[user.role] || user.role;
  return <header className="admin-topbar">
    <button className="admin-menu-button" onClick={onMenu} aria-label="Открыть меню"><Menu /></button>
    <form className="admin-global-search" onSubmit={submit}>
      <Search aria-hidden="true" />
      <label className="sr-only" htmlFor="admin-global-search">Найти запись</label>
      <input ref={searchRef} id="admin-global-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Найти запись" />
      <kbd>Ctrl + K</kbd>
    </form>
    <button className="admin-help" type="button" title="Справка по панели" aria-label="Справка"><CircleHelp /></button>
    <div className="admin-profile-wrap" ref={profileRef}>
      <button
        ref={profileButtonRef}
        className="admin-profile"
        type="button"
        aria-label={`Открыть меню профиля: ${user.displayName}`}
        aria-haspopup="true"
        aria-expanded={profileOpen}
        aria-controls="admin-profile-panel"
        onClick={() => { setLogoutError(''); setProfileOpen((value) => !value); }}
      >
        <CircleUserRound aria-hidden="true" />
        <span>{user.displayName}</span>
        <ChevronDown className="admin-profile-chevron" aria-hidden="true" />
      </button>
      {profileOpen && <div id="admin-profile-panel" className="admin-profile-menu" aria-label="Меню профиля">
        <div className="admin-profile-summary">
          <div className="admin-profile-avatar" aria-hidden="true">{user.displayName.slice(0, 1).toUpperCase()}</div>
          <div><strong>{user.displayName}</strong><span>{user.email}</span><small>{roleLabel}</small></div>
        </div>
        <nav aria-label="Профиль администратора">
          <Link to="/account" onClick={() => setProfileOpen(false)}><CircleUserRound /> Профиль редакции</Link>
          <Link to="/" onClick={() => setProfileOpen(false)}><ExternalLink /> Открыть сайт</Link>
        </nav>
        <button className="admin-profile-logout" type="button" onClick={logout}><LogOut /> Выйти</button>
        {logoutError && <p className="admin-profile-error" role="alert">{logoutError}</p>}
      </div>}
    </div>
  </header>;
}

function AdminSidebar({ open, onClose }) {
  const { user } = useAuth();
  const items = [["",Gauge,'Обзор'],['records',Trophy,'Рекорды'],['protocols',FileText,'Протоколы'],['players',CircleUserRound,'Игроки и фото'],['news',Newspaper,'Новости'],['predictions',Target,'Прогнозы'],['matches',CalendarDays,'Матчи']];
  if (user.role === 'admin') items.push(['users',Users,'Пользователи']);
  items.push(['media',ImagePlus,'Медиа']);
  return <>
    {open && <button className="admin-sidebar-scrim" onClick={onClose} aria-label="Закрыть меню" />}
    <aside className={`admin-sidebar${open ? ' is-open' : ''}`}>
      <div className="admin-title"><div><b><em>OVI</em> STATS</b><span>Админка</span></div></div>
      <nav>{items.map(([path,Icon,label]) => <NavLink key={label} end={!path} onClick={onClose} to={`/admin${path ? `/${path}` : ''}`}><Icon />{label}</NavLink>)}</nav>
      <Link className="back-site" to="/"><ExternalLink /> Вернуться на сайт</Link>
    </aside>
  </>;
}

function AdminOverview() {
  const [data, setData] = useState(null);
  useEffect(() => { api('/admin/overview').then(setData); }, []);
  const labels = { records:'Рекордов', players:'Игроков', protocols:'Протоколов', news:'Новостей', predictions:'Прогнозов', matches:'Матчей', users:'Пользователей' };
  return <AdminPageHead title="Обзор" text="Рекорды, материалы и пользователи">{!data ? <Loading /> : <div className="admin-stats">{Object.entries(data.counts).map(([key,value]) => <div key={key}><span>{labels[key]}</span><strong>{Number(value).toLocaleString('ru-RU')}</strong></div>)}</div>}</AdminPageHead>;
}

function AdminPageHead({ title, text, action, children }) { return <><div className="admin-page-head"><div><div className="eyebrow muted">OVI Stats · Редакция</div><h1>{title}</h1><p>{text}</p></div>{action}</div>{children}</>; }

function AdminRecords() {
  const { user } = useAuth();
  const [urlParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [bootstrap, setBootstrap] = useState(null);
  const [players, setPlayers] = useState([]);
  const [qDraft, setQDraft] = useState(urlParams.get('adminq') || '');
  const [q, setQ] = useState(urlParams.get('adminq') || '');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ galaxy: '', stage: '', access: '', status: '' });
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const initialRecordOpened = useRef(false);
  const drawerElementRef = useRef(null);
  const drawerResizeRef = useRef(null);
  const drawerStorageKey = `ovi-admin-record-drawer-width:v1:${user?.id || 'editor'}`;
  const [drawerWidth, setDrawerWidth] = useState(() => {
    try {
      const storedWidth = Number(window.localStorage.getItem(`ovi-admin-record-drawer-width:v1:${user?.id || 'editor'}`));
      return Number.isFinite(storedWidth) && storedWidth > 0 ? clampAdminDrawerWidth(storedWidth) : ADMIN_DRAWER_DEFAULT_WIDTH;
    } catch {
      return ADMIN_DRAWER_DEFAULT_WIDTH;
    }
  });
  const [isResizingDrawer, setIsResizingDrawer] = useState(false);
  const [renderedDrawerWidth, setRenderedDrawerWidth] = useState(drawerWidth);

  useEffect(() => {
    const persistTimer = window.setTimeout(() => {
      try { window.localStorage.setItem(drawerStorageKey, String(drawerWidth)); } catch { /* Browser privacy settings may disable persistent storage. */ }
    }, 120);
    return () => window.clearTimeout(persistTimer);
  }, [drawerStorageKey, drawerWidth]);

  useEffect(() => {
    const drawer = drawerElementRef.current;
    if (!drawer) return undefined;
    const updateRenderedWidth = () => setRenderedDrawerWidth(Math.round(drawer.getBoundingClientRect().width));
    updateRenderedWidth();
    const observer = new ResizeObserver(updateRenderedWidth);
    observer.observe(drawer);
    return () => observer.disconnect();
  }, [selected?.id]);

  useEffect(() => () => {
    const resize = drawerResizeRef.current;
    if (!resize) return;
    window.removeEventListener('pointermove', resize.move);
    window.removeEventListener('pointerup', resize.finish);
    window.removeEventListener('pointercancel', resize.finish);
  }, []);

  useEffect(() => {
    Promise.all([api('/bootstrap'), api('/admin/players')]).then(([bootstrapData, playerData]) => {
      setBootstrap(bootstrapData);
      setPlayers(playerData.items);
    }).catch((requestError) => setError(requestError.message));
  }, []);

  useEffect(() => {
    const nextQuery = urlParams.get('adminq') || '';
    setQDraft(nextQuery);
    setQ(nextQuery);
    setPage(1);
    if (recordNumberFromQuery(nextQuery) !== null) setFilters((current) => ({ ...current, galaxy: '', stage: '' }));
  }, [urlParams]);

  useEffect(() => {
    const query = new URLSearchParams({ q, page: String(page), pageSize: '25' });
    for (const [key, value] of Object.entries(filters)) if (value && key !== 'status') query.set(key, value);
    setError('');
    api(`/admin/records?${query}`).then(async (result) => {
      setData(result);
      if (!initialRecordOpened.current && result.items.length) {
        initialRecordOpened.current = true;
        const initial = result.items.find((item) => item.id === 4) || result.items[0];
        setSelected(editableRecord(await api(`/admin/records/${initial.id}`)));
      }
    }).catch((requestError) => setError(requestError.message));
  }, [filters, page, q]);

  function submitSearch(event) {
    event.preventDefault();
    const query = qDraft.trim();
    setPage(1);
    if (recordNumberFromQuery(query) !== null) setFilters((current) => ({ ...current, galaxy: '', stage: '' }));
    setQ(query);
  }
  function updateFilter(key, value) { setPage(1); setFilters((current) => ({ ...current, [key]: value })); }
  async function openRecord(item) { setMessage(''); setSelected(editableRecord(await api(`/admin/records/${item.id}`))); }
  function createRecord() {
    const defaultPlayers = ['ОВЕЧКИН', 'ГРЕТЦКИ', 'ХОУ'].map((needle) => players.find((player) => player.name.includes(needle))).filter(Boolean);
    setSelected(editableRecord({ id: null, name: '', title: '', galaxyId: bootstrap?.galaxies[0]?.id || 1, stage: 'РЧ', accessTier: 'free', placements: defaultPlayers.map((player, index) => ({ rank: index + 1, playerId: player.id, playerName: player.name, photoUrl: player.photoUrl, value: '' })) }));
    setMessage('');
  }
  async function saveRecord(event) {
    event.preventDefault();
    setError('');
    try {
      const method = selected.id ? 'PUT' : 'POST';
      const path = selected.id ? `/admin/records/${selected.id}` : '/admin/records';
      const payload = { ...selected, placements: selected.placements.map(({ editorKey, ...place }) => place) };
      const result = await api(path, { method, body: JSON.stringify(payload) });
      const id = selected.id || result.id;
      setSelected(editableRecord(await api(`/admin/records/${id}`)));
      setMessage('Изменения сохранены');
      const query = new URLSearchParams({ q, page: String(page), pageSize: '25' });
      for (const [key, value] of Object.entries(filters)) if (value && key !== 'status') query.set(key, value);
      setData(await api(`/admin/records?${query}`));
    } catch (requestError) { setError(requestError.message); }
  }
  async function uploadPlacementPhoto(event, playerId) {
    const file = event.target.files?.[0];
    if (!file || !playerId) return;
    const form = new FormData();
    form.append('photo', file);
    try {
      const result = await api(`/admin/players/${playerId}/photo`, { method: 'POST', body: form });
      setPlayers((items) => items.map((player) => player.id === playerId ? { ...player, photoUrl: result.photoUrl } : player));
      setSelected((record) => ({ ...record, placements: record.placements.map((place) => place.playerId === playerId ? { ...place, photoUrl: result.photoUrl } : place) }));
      setMessage('Фотография обновлена');
    } catch (requestError) { setError(requestError.message); }
  }
  async function exportRecords() {
    if (!data) return;
    const base = new URLSearchParams({ q, pageSize: '100' });
    for (const [key, value] of Object.entries(filters)) if (value && key !== 'status') base.set(key, value);
    const first = await api(`/admin/records?${base}`);
    const requests = Array.from({ length: first.pages }, (_, index) => index ? api(`/admin/records?${new URLSearchParams({ ...Object.fromEntries(base), page: String(index + 1) })}`) : Promise.resolve(first));
    const rows = (await Promise.all(requests)).flatMap((result) => result.items);
    const csv = [['№', 'Рекорд', 'Галактика', 'Этап', 'Доступ'], ...rows.map((item) => [item.id, item.name, item.galaxyName, item.stage, 'Открыт'])]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replaceAll('"', '""')}"`).join(';')).join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
    link.download = 'ovi-records.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  }
  function toggleChecked(id) { setChecked((items) => items.includes(id) ? items.filter((value) => value !== id) : [...items, id]); }

  function startDrawerResize(event) {
    if (event.button !== 0) return;
    event.preventDefault();
    const currentWidth = drawerElementRef.current?.getBoundingClientRect().width || drawerWidth;
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const move = (pointerEvent) => {
      if (pointerEvent.pointerId !== pointerId) return;
      setDrawerWidth(clampAdminDrawerWidth(currentWidth + startX - pointerEvent.clientX));
    };
    const finish = (pointerEvent) => {
      if (pointerEvent.pointerId !== pointerId) return;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      drawerResizeRef.current = null;
      setIsResizingDrawer(false);
    };
    drawerResizeRef.current = { move, finish };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    setIsResizingDrawer(true);
  }

  function resizeDrawerWithKeyboard(event) {
    const step = event.shiftKey ? 48 : 24;
    const currentWidth = drawerElementRef.current?.getBoundingClientRect().width || renderedDrawerWidth;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setDrawerWidth(clampAdminDrawerWidth(currentWidth + step));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setDrawerWidth(clampAdminDrawerWidth(currentWidth - step));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setDrawerWidth(ADMIN_DRAWER_DEFAULT_WIDTH);
    } else if (event.key === 'End') {
      event.preventDefault();
      setDrawerWidth(ADMIN_DRAWER_MAX_WIDTH);
    }
  }

  return <div
    className={`admin-records-screen${selected ? ' has-drawer' : ''}${isResizingDrawer ? ' is-resizing-drawer' : ''}`}
    style={{ '--admin-record-drawer-width': `${drawerWidth}px` }}
  >
    <section className="admin-records-main">
      <div className="admin-records-heading">
        <div><h1>Рекорды</h1><p>{(data?.total || 1124).toLocaleString('ru-RU')} записи · обновлено сегодня</p></div>
        <div className="admin-records-commands">
          <button type="button" disabled title="Импорт выполняется только через проверенную миграцию"><Upload /> Импорт XLS</button>
          <button type="button" onClick={exportRecords}><Download /> Экспорт</button>
          <button type="button" className="primary" onClick={createRecord}>Добавить рекорд <Plus /></button>
        </div>
      </div>
      <form className="admin-records-filters" onSubmit={submitSearch}>
        <label><span className="sr-only">Галактика</span><select value={filters.galaxy} onChange={(event) => updateFilter('galaxy', event.target.value)}><option value="">Галактика　Все</option>{bootstrap?.galaxies.map((galaxy) => <option key={galaxy.id} value={galaxy.slug}>{galaxy.name}</option>)}</select></label>
        <label><span className="sr-only">Этап</span><select value={filters.stage} onChange={(event) => updateFilter('stage', event.target.value)}><option value="">Этап　Все</option><option value="РЧ">РЧ</option><option value="КС">КС</option><option value="РЧ+КС">РЧ+КС</option></select></label>
        <label><span className="sr-only">Статус</span><select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}><option value="">Статус　Все</option><option value="published">Опубликован</option></select></label>
        <label className="admin-records-search"><Search /><span className="sr-only">Поиск по рекордам</span><input value={qDraft} onChange={(event) => setQDraft(event.target.value)} placeholder="Поиск по рекордам..." /></label>
        <button className="admin-filter-button" aria-label="Применить поиск"><Filter /></button>
      </form>
      {error && <div className="admin-inline-error">{error}</div>}
      {!data ? <Loading /> : <>
        <div className="admin-records-table-scroll">
          <table className="admin-records-table">
            <thead><tr><th><input type="checkbox" aria-label="Выбрать все записи на странице" checked={data.items.length > 0 && data.items.every((item) => checked.includes(item.id))} onChange={() => setChecked(data.items.every((item) => checked.includes(item.id)) ? [] : data.items.map((item) => item.id))} /></th><th>№ <ArrowDownUp /></th><th>Рекорд <ArrowDownUp /></th><th>Галактика <ArrowDownUp /></th><th>Этап <ArrowDownUp /></th><th>Доступ <ArrowDownUp /></th><th>Обновлён <ArrowDownUp /></th><th>Действия</th></tr></thead>
            <tbody>{data.items.map((item) => <tr key={item.id} className={selected?.id === item.id ? 'selected' : ''}>
              <td><input type="checkbox" aria-label={`Выбрать рекорд ${item.id}`} checked={checked.includes(item.id)} onChange={() => toggleChecked(item.id)} /></td>
              <td>{item.id}</td><td><b>{item.name}</b></td><td>{item.galaxyName}</td><td>{item.stage}</td>
              <td><span className="admin-tier free">Открыт</span></td>
              <td>{formatDate(item.updatedAt, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '')}</td>
              <td><div className="admin-row-actions"><Link to={`/records/${item.id}`} aria-label={`Посмотреть рекорд ${item.id}`}><Eye /></Link><button onClick={() => openRecord(item)} aria-label={`Редактировать рекорд ${item.id}`}><Pencil /></button><button aria-label="Другие действия" title="Другие действия"><MoreHorizontal /></button></div></td>
            </tr>)}</tbody>
          </table>
        </div>
        <div className="admin-records-footer">
          <div className="admin-bulk"><span>Выбрано: {checked.length}</span><button onClick={() => setChecked([])}>Сбросить</button><button disabled>Изменить доступ <ChevronDown /></button><button disabled title="Удаление исходного каталога заблокировано"><Trash2 /> Удалить</button></div>
          <span>{(data.page - 1) * data.pageSize + 1}–{Math.min(data.page * data.pageSize, data.total)} из {data.total.toLocaleString('ru-RU')}</span>
          <ReferenceAdminPagination page={data.page} pages={data.pages} onPage={setPage} />
        </div>
      </>}
    </section>
    {selected && <AdminRecordDrawer
      key={selected.id ?? 'new'}
      ref={drawerElementRef}
      record={selected}
      setRecord={setSelected}
      players={players}
      galaxies={bootstrap?.galaxies || []}
      message={message}
      error={error}
      drawerWidth={renderedDrawerWidth}
      onClose={() => setSelected(null)}
      onSave={saveRecord}
      onUploadPhoto={uploadPlacementPhoto}
      onReorder={() => setMessage('')}
      onResizeStart={startDrawerResize}
      onResizeKeyDown={resizeDrawerWithKeyboard}
      onResizeReset={() => setDrawerWidth(ADMIN_DRAWER_DEFAULT_WIDTH)}
    />}
  </div>;
}

function ReferenceAdminPagination({ page, pages, onPage }) {
  const values = [1, 2, 3, page, pages].filter((value) => value >= 1 && value <= Math.max(1, pages));
  const unique = [...new Set(values)].sort((a, b) => a - b);
  return <nav className="admin-reference-pagination" aria-label="Страницы записей"><button disabled={page <= 1} onClick={() => onPage(page - 1)}><ChevronLeft /></button>{unique.map((value, index) => <React.Fragment key={value}>{index > 0 && value - unique[index - 1] > 1 && <i>…</i>}<button className={page === value ? 'active' : ''} onClick={() => onPage(value)}>{value}</button></React.Fragment>)}<button disabled={page >= pages} onClick={() => onPage(page + 1)}><ChevronRight /></button></nav>;
}

const AdminRecordDrawer = React.forwardRef(function AdminRecordDrawer({
  record, setRecord, players, galaxies, message, error, drawerWidth, onClose, onSave, onUploadPhoto,
  onResizeStart, onResizeKeyDown, onResizeReset, onReorder,
}, ref) {
  const dragSource = useRef(null);
  const [draggingKey, setDraggingKey] = useState(null);
  const [dropKey, setDropKey] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  function reorder(from, to) {
    if (from === to || from < 0 || to < 0 || to >= record.placements.length) return;
    const placement = record.placements[from];
    if (!placement) return;
    setRecord((current) => ({ ...current, placements: movePlacement(current.placements, from, to) }));
    onReorder();
    setAnnouncement(`${placement.playerName || 'Игрок'}: ${to + 1} место. Сохраните изменения.`);
  }
  function finishDrag() { dragSource.current = null; setDraggingKey(null); setDropKey(null); }
  function dragTarget(event) {
    const card = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-placement-key]');
    return card?.closest('.admin-record-drawer') === event.currentTarget.closest('.admin-record-drawer')
      ? card.dataset.placementKey : null;
  }
  function startDrag(event, place) {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragSource.current = { key: place.editorKey, pointerId: event.pointerId, x: event.clientX, y: event.clientY, active: false };
  }
  function moveDrag(event) {
    const source = dragSource.current;
    if (!source || source.pointerId !== event.pointerId) return;
    if (!source.active && Math.hypot(event.clientX - source.x, event.clientY - source.y) < 5) return;
    source.active = true;
    setDraggingKey(source.key);
    setDropKey(dragTarget(event));
  }
  function dropPlacement(event) {
    const source = dragSource.current;
    if (!source || source.pointerId !== event.pointerId) return;
    if (source.active) {
      const target = dragTarget(event);
      reorder(record.placements.findIndex((place) => place.editorKey === source.key), record.placements.findIndex((place) => place.editorKey === target));
    }
    finishDrag();
  }
  function updatePlacement(index, patch) {
    setRecord((current) => ({ ...current, placements: current.placements.map((place, placeIndex) => placeIndex === index ? { ...place, ...patch } : place) }));
  }
  function changePlayer(index, playerId) {
    if (['unknown', 'vacant'].includes(playerId)) {
      updatePlacement(index, { emptyState: playerId, playerId: null, playerName: playerId === 'vacant' ? 'Место вакантно' : 'Нет данных', photoUrl: null, value: playerId === 'vacant' ? 0 : null });
      return;
    }
    const player = players.find((item) => item.id === Number(playerId));
    updatePlacement(index, { emptyState: null, playerId: player?.id, playerName: player?.name, photoUrl: player?.photoUrl, marker: player?.marker, value: record.placements[index].emptyState ? '' : record.placements[index].value });
  }
  function addPlacement() {
    const used = new Set(record.placements.map((place) => place.playerId));
    const player = players.find((item) => !used.has(item.id)) || players[0];
    if (!player) return;
    setRecord((current) => ({ ...current, placements: [...current.placements, { rank: current.placements.length + 1, playerId: player.id, playerName: player.name, photoUrl: player.photoUrl, value: '', editorKey: crypto.randomUUID() }] }));
  }
  function removePlacement(index) {
    setRecord((current) => ({ ...current, placements: current.placements.filter((_, placeIndex) => placeIndex !== index).map((place, placeIndex) => ({ ...place, rank: placeIndex + 1 })) }));
  }
  return <aside ref={ref} className="admin-record-drawer" aria-label={record.id ? 'Редактирование рекорда' : 'Добавление рекорда'}>
    <button
      type="button"
      className="admin-drawer-resizer"
      role="separator"
      aria-label="Изменить ширину редактора"
      aria-orientation="vertical"
      aria-valuemin={ADMIN_DRAWER_MIN_WIDTH}
      aria-valuemax={ADMIN_DRAWER_MAX_WIDTH}
      aria-valuenow={drawerWidth}
      aria-valuetext={`${drawerWidth} пикселей`}
      title="Потяните для изменения ширины. Двойной щелчок — стандартная ширина"
      onPointerDown={onResizeStart}
      onKeyDown={onResizeKeyDown}
      onDoubleClick={onResizeReset}
    ><span aria-hidden="true" /></button>
    <div className="admin-record-drawer-scroll">
      <div className="admin-drawer-title"><h2>{record.id ? 'Редактирование рекорда' : 'Новый рекорд'}</h2><button onClick={onClose} aria-label="Закрыть редактор"><X /></button></div>
      <form onSubmit={onSave}>
      <label className="admin-drawer-field"><span>Название <em>*</em></span><input value={record.name} maxLength="100" onChange={(event) => setRecord({ ...record, name: event.target.value })} required /><small>{record.name.length} / 100</small></label>
      <label className="admin-drawer-field"><span>Короткое имя / титул <em>*</em></span><input value={record.title} maxLength="180" onChange={(event) => setRecord({ ...record, title: event.target.value })} required /><small>{record.title.length} / 180</small></label>
      <div className="admin-drawer-pair">
        <label className="admin-drawer-field"><span>Галактика <em>*</em></span><select value={record.galaxyId} disabled={Boolean(record.id)} onChange={(event) => setRecord({ ...record, galaxyId: Number(event.target.value) })}>{galaxies.map((galaxy) => <option key={galaxy.id} value={galaxy.id}>{galaxy.name}</option>)}</select></label>
        <label className="admin-drawer-field"><span>Этап <em>*</em></span><select value={record.stage} onChange={(event) => setRecord({ ...record, stage: event.target.value })}><option>РЧ</option><option>КС</option><option>РЧ+КС</option></select></label>
      </div>
      <div className="admin-drawer-pair">
        <label className="admin-drawer-field"><span>Статус</span><select value="published" disabled><option value="published">Опубликован</option></select></label>
      </div>
      <div className="admin-placements-editor">
        <span className="sr-only" role="status" aria-live="polite">{announcement}</span>
        {record.placements.map((place, index) => {
          const player = players.find((item) => item.id === place.playerId);
          const photoUrl = player?.photoUrl || place.photoUrl;
          return <fieldset key={place.editorKey} data-placement-key={place.editorKey} className={`admin-placement-card${draggingKey === place.editorKey ? ' is-dragging' : ''}${dropKey === place.editorKey && dropKey !== draggingKey ? ' is-drop-target' : ''}`}>
            <legend>{place.rank} место{place.rank === 1 ? ' (лидер)' : ''}</legend>
            <div className="admin-placement-move">
              <button type="button" disabled={index === 0} onClick={() => reorder(index, index - 1)} aria-label={`Поднять ${place.playerName} выше`} title="На место выше"><ChevronUp aria-hidden="true" /></button>
              <button type="button" className="admin-placement-handle" onPointerDown={(event) => startDrag(event, place)} onPointerMove={moveDrag} onPointerUp={dropPlacement} onPointerCancel={finishDrag} onLostPointerCapture={finishDrag}
                onKeyDown={(event) => { if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); reorder(index, index + (event.key === 'ArrowUp' ? -1 : 1)); } }}
                aria-label={`Переместить ${place.playerName}, сейчас ${place.rank} место`} title="Перетащите карточку или используйте стрелки вверх/вниз"><GripVertical aria-hidden="true" /></button>
              <button type="button" disabled={index === record.placements.length - 1} onClick={() => reorder(index, index + 1)} aria-label={`Опустить ${place.playerName} ниже`} title="На место ниже"><ChevronDown aria-hidden="true" /></button>
            </div>
            <div className="admin-placement-photo">{photoUrl ? <img src={photoUrl} alt="" draggable={false} /> : <span>{place.playerName?.slice(0, 1) || '?'}</span>}{!place.emptyState && <label><Camera /> Изменить<input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => onUploadPhoto(event, place.playerId)} /></label>}<button type="button" onClick={() => removePlacement(index)} aria-label={`Удалить ${place.rank} место`}><Trash2 /></button></div>
            <div className="admin-placement-fields"><label><span>Игрок <em>*</em></span><select value={place.emptyState || place.playerId} onChange={(event) => changePlayer(index, event.target.value)}><option value="unknown">Нет данных</option><option value="vacant">Место вакантно</option>{players.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label><label><span>Значение <em>*</em></span><input type="number" step="any" value={place.value ?? ''} onChange={(event) => updatePlacement(index, { value: event.target.value })} disabled={Boolean(place.emptyState)} required={!place.emptyState} /></label></div>
          </fieldset>;
        })}
        <button type="button" className="admin-add-placement" onClick={addPlacement}><Plus /> Добавить место</button>
      </div>
      {error && <div className="admin-inline-error">{error}</div>}
      <div className="admin-drawer-actions"><button className="primary" type="submit">Сохранить</button><button type="button" onClick={onClose}>Отменить</button></div>
      {message && <div className="admin-save-message"><CheckCircle2 /> {message}<X aria-hidden="true" /></div>}
      </form>
    </div>
  </aside>;
});

function AdminPlayers({ title = 'Игроки' }) {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const load = () => api('/admin/players').then(({ items: nextItems }) => setItems(nextItems));
  useEffect(() => { load().catch((requestError) => setError(requestError.message)); }, []);
  const visible = items?.filter((item) => item.name.toLowerCase().includes(q.toLowerCase()));

  async function save(event) {
    event.preventDefault();
    setError('');
    try {
      await api(`/admin/players/${selected.id}`, { method: 'PUT', body: JSON.stringify(selected) });
      setSelected(null);
      setMessage('Карточка игрока сохранена');
      await load();
    } catch (requestError) { setError(requestError.message); }
  }

  async function uploadPhoto(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { setError('Разрешены только JPG, PNG и WebP'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Изображение должно быть не больше 5 МБ'); return; }
    setUploading(true);
    setError('');
    setMessage('');
    const form = new FormData();
    form.append('photo', file);
    try {
      const result = await api(`/admin/players/${selected.id}/photo`, { method: 'POST', body: form });
      setSelected((current) => ({ ...current, photoUrl: result.photoUrl }));
      setMessage('Фотография загружена и уже используется в рекордах');
      await load();
    } catch (requestError) { setError(requestError.message); }
    finally { setUploading(false); }
  }

  return <AdminPageHead title={title} text="Имена, статусы и фотографии участников рейтингов">
    {message && <div className="admin-calendar-message" aria-live="polite"><CheckCircle2 />{message}</div>}
    {error && <div className="form-error" role="alert">{error}</div>}
    <label className="admin-search"><Search /><input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Найти игрока" /></label>
    {!visible ? <Loading /> : <div className="player-admin-grid">{visible.map((player) => <button className="player-admin-card" key={player.id} onClick={() => { setSelected({ ...player }); setError(''); setMessage(''); }}>{player.photoUrl ? <img src={player.photoUrl} alt="" /> : <div className="player-avatar placeholder">{player.name.slice(0, 1)}</div>}<span><b>{player.name}</b><small>{statusLabel[player.status] || player.status}</small></span><Pencil /></button>)}</div>}
    {selected && <div className="drawer-backdrop"><aside className="edit-drawer"><button type="button" className="drawer-close" onClick={() => setSelected(null)} aria-label="Закрыть карточку"><X /></button><h2>Карточка игрока</h2><div className="photo-editor">{selected.photoUrl ? <img src={selected.photoUrl} alt={`Фото: ${selected.name}`} /> : <div className="player-avatar placeholder">{selected.name.slice(0, 1)}</div>}<label className="button button-outline dark"><Upload />{uploading ? 'Загружаем…' : 'Загрузить фото'}<input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={uploadPhoto} /></label></div>{message && <div className="form-message" aria-live="polite">{message}</div>}{error && <div className="form-error" role="alert">{error}</div>}<form onSubmit={save}><Field label="Имя" value={selected.name} onChange={(value) => setSelected({ ...selected, name: value })} /><label className="field"><span>Статус</span><select value={selected.status} onChange={(event) => setSelected({ ...selected, status: event.target.value })}><option value="active">Действующий</option><option value="veteran">Ветеран</option><option value="deceased">Ушёл из жизни</option></select></label><Field label="Пометка источника" value={selected.marker || ''} onChange={(value) => setSelected({ ...selected, marker: value })} hint="Служебное сокращение из исходной таблицы; можно оставить пустым." required={false} /><button className="button">Сохранить</button></form></aside></div>}
  </AdminPageHead>;
}

const emptyContent = {
  news:{title:'',slug:'',excerpt:'',body:'',image_url:'',source_url:'',access_tier:'free',status:'draft',needs_review:true},
  predictions:{title:'',summary:'',rationale:'',confidence:50,access_tier:'free',status:'draft'},
  matches:{opponent:'',starts_at:new Date().toISOString().slice(0,16),venue:'',status:'scheduled',result:''},
};
function ContentManager({kind,title}) {
  const [items,setItems]=useState(null);const [editing,setEditing]=useState(null);const [imageFile,setImageFile]=useState(null);const [imagePreview,setImagePreview]=useState('');const [imageRemoved,setImageRemoved]=useState(false);const [saveError,setSaveError]=useState('');const [saving,setSaving]=useState(false);
  const load=()=>api(`/admin/content/${kind}`).then(({items})=>setItems(items));useEffect(()=>{load();},[kind]);
  useEffect(()=>()=>{if(imagePreview)URL.revokeObjectURL(imagePreview);},[imagePreview]);
  function openEditor(item){setImageFile(null);setImagePreview('');setImageRemoved(false);setSaveError('');setEditing({...item});}
  function closeEditor(){setEditing(null);setImageFile(null);setImagePreview('');setImageRemoved(false);setSaveError('');}
  function chooseNewsImage(event){const file=event.target.files?.[0];if(!file)return;if(!['image/jpeg','image/png','image/webp'].includes(file.type)){setSaveError('Разрешены только JPG, PNG и WebP');event.target.value='';return;}if(file.size>5*1024*1024){setSaveError('Изображение должно быть не больше 5 МБ');event.target.value='';return;}setSaveError('');setImageFile(file);setImageRemoved(false);setImagePreview(URL.createObjectURL(file));event.target.value='';}
  function removeNewsImage(){setImageFile(null);setImagePreview('');setImageRemoved(true);}
  async function save(e){e.preventDefault();setSaving(true);setSaveError('');let savedId=editing.id;let savedSlug=editing.slug;try{const method=editing.id?'PUT':'POST';const path=editing.id?`/admin/content/${kind}/${editing.id}`:`/admin/content/${kind}`;const result=await api(path,{method,body:JSON.stringify(editing)});savedId=editing.id||Number(result.id);savedSlug=result?.slug||savedSlug;if(kind==='news'){if(imageRemoved&&editing.image_url&&!imageFile)await api(`/admin/news/${savedId}/image`,{method:'DELETE'});if(imageFile){const form=new FormData();form.append('image',imageFile);await api(`/admin/news/${savedId}/image`,{method:'POST',body:form});}}closeEditor();await load();}catch(requestError){if(savedId&&!editing.id)setEditing(current=>({...current,id:savedId,slug:savedSlug}));setSaveError(requestError.message);}finally{setSaving(false);}}
  async function remove(id){if(!confirm('Удалить материал?'))return;await api(`/admin/content/${kind}/${id}`,{method:'DELETE'});load();}
  const visibleNewsImage=kind==='news'?(imagePreview||(!imageRemoved&&editing?.image_url)||''):'';
  return <AdminPageHead title={title} text="Черновики и опубликованные материалы" action={<button className="button" onClick={()=>openEditor(emptyContent[kind])}>Добавить</button>}>{!items?<Loading/>:<div className="content-admin-list">{items.map(item=><article key={item.id}><div><span className={`status ${item.status}`}>{item.status}</span><h2>{item.title||item.opponent}</h2><p>{item.excerpt||item.summary||formatDate(item.starts_at)}</p></div><div><button className="row-action" onClick={()=>openEditor(item)} aria-label={`Редактировать ${item.title||item.opponent}`}><Pencil/></button><button className="row-action danger" onClick={()=>remove(item.id)} aria-label={`Удалить ${item.title||item.opponent}`}><X/></button></div></article>)}{!items.length&&<EmptyState title="Пока пусто" text="Создайте первый материал."/>}</div>}{editing&&<div className="drawer-backdrop"><aside className="edit-drawer wide"><button type="button" className="drawer-close" onClick={closeEditor} aria-label="Закрыть редактор"><X/></button><h2>{editing.id?'Редактирование':'Новый материал'}</h2><form onSubmit={save}>{kind==='news'&&<><div className="news-cover-editor"><span>Обложка новости</span><div className="news-cover-preview">{visibleNewsImage?<img src={visibleNewsImage} alt="Предпросмотр обложки"/>:<div><ImagePlus/><b>Фото не выбрано</b><small>Рекомендуем горизонтальное изображение 16:9</small></div>}</div><div className="news-cover-actions"><label className="button button-outline dark"><Upload/>{visibleNewsImage?'Заменить фото':'Добавить фото'}<input type="file" accept="image/jpeg,image/png,image/webp" onChange={chooseNewsImage}/></label>{visibleNewsImage&&<button type="button" className="button button-outline dark danger" onClick={removeNewsImage}><Trash2/>Удалить</button>}</div><small>JPG, PNG или WebP, не больше 5 МБ.</small></div><Field label="Заголовок" value={editing.title} onChange={v=>setEditing(current=>({...current,title:v,slug:current.id?current.slug:toRouteSlug(v)}))}/><div className="news-route-field"><span>Адрес материала</span><div><code>/news/{editing.slug||toRouteSlug(editing.title)||'...'}</code></div><small>{editing.id?'Адрес создан автоматически и остаётся постоянным, даже если заголовок изменится.':'Формируется автоматически из заголовка. При совпадении адресов добавится номер.'}</small></div><TextField label="Анонс" value={editing.excerpt} onChange={v=>setEditing({...editing,excerpt:v})}/><TextField label="Текст" value={editing.body} onChange={v=>setEditing({...editing,body:v})} rows={8}/><Field label="Ссылка на источник" type="url" value={editing.source_url||''} onChange={v=>setEditing({...editing,source_url:v})} required={false}/></>}{kind==='predictions'&&<><Field label="Заголовок" value={editing.title} onChange={v=>setEditing({...editing,title:v})}/><TextField label="Краткий прогноз" value={editing.summary} onChange={v=>setEditing({...editing,summary:v})}/><TextField label="Обоснование" value={editing.rationale} onChange={v=>setEditing({...editing,rationale:v})} rows={8}/><Field label="Уверенность, %" type="number" value={editing.confidence} onChange={v=>setEditing({...editing,confidence:v})}/></>}{kind==='matches'&&<><Field label="Соперник" value={editing.opponent} onChange={v=>setEditing({...editing,opponent:v})}/><Field label="Дата и время" type="datetime-local" value={String(editing.starts_at).slice(0,16)} onChange={v=>setEditing({...editing,starts_at:v})}/><Field label="Место" value={editing.venue||''} onChange={v=>setEditing({...editing,venue:v})}/><Field label="Результат" value={editing.result||''} onChange={v=>setEditing({...editing,result:v})}/></>} {kind!=='matches'&&<div className="form-row"><p>Опубликованные материалы открыты всем.</p><label className="field"><span>Статус</span><select value={editing.status} onChange={e=>setEditing({...editing,status:e.target.value})}><option value="draft">Черновик</option><option value="published">Опубликован</option></select></label></div>}{saveError&&<div className="form-error" role="alert">{saveError}</div>}<button className="button" disabled={saving}>{saving?'Сохраняем…':'Сохранить'}</button></form></aside></div>}</AdminPageHead>;
}
function TextField({label,value,onChange,rows=4}){return <label className="field"><span>{label}</span><textarea value={value} rows={rows} onChange={e=>onChange(e.target.value)} required/></label>}

function AdminCalendarLogo({ abbr, url }) {
  return <span className="admin-calendar-logo" aria-hidden="true"><img src={url || `/media/teams/${abbr}.svg`} alt="" decoding="async" onError={(event) => { event.currentTarget.hidden = true; }} /></span>;
}

function adminCalendarDate(value) {
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', weekday: 'short' }).format(new Date(`${value}T12:00:00`));
}

function AdminCalendar() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');
  const [team, setTeam] = useState('');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let active = true;
    const params = new URLSearchParams();
    if (month) params.set('month', month);
    if (team) params.set('team', team);
    setError('');
    api(`/admin/calendar?${params}`).then((result) => { if (active) setData(result); }).catch((requestError) => { if (active) setError(requestError.message); });
    return () => { active = false; };
  }, [month, team]);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleItems = useMemo(() => (data?.items || []).filter((item) => !normalizedQuery
    || `${item.awayTeam} ${item.awayName} ${item.homeTeam} ${item.homeName} ${item.date}`.toLowerCase().includes(normalizedQuery)), [data, normalizedQuery]);
  const filledTimes = data?.items.filter((item) => item.startTime).length || 0;

  async function saveTime(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const result = await api(`/admin/calendar/${encodeURIComponent(editing.id)}`, { method: 'PUT', body: JSON.stringify({ startTime: editing.startTime || '' }) });
      setData((current) => ({ ...current, items: current.items.map((item) => item.id === result.item.id ? result.item : item) }));
      setEditing(result.item);
      setMessage(result.item.startTime ? `Время ${result.item.startTime} МСК сохранено и опубликовано` : 'Время удалено из публичного календаря');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  return <AdminPageHead title="Календарь матчей" text="Расписание и время начала матчей (МСК)" action={<Link className="button" to="/calendar" target="_blank" rel="noreferrer">Открыть календарь <ExternalLink /></Link>}>
    {message && <div className="admin-calendar-message" aria-live="polite"><CheckCircle2 />{message}</div>}
    {error && <div className="form-error" role="alert">{error}</div>}
    <div className="admin-calendar-toolbar">
      <label><span>Месяц</span><select value={month || data?.month || ''} onChange={(event) => { setMonth(event.target.value); setEditing(null); setMessage(''); }}>{data?.availableMonths.map((value) => <option key={value} value={value}>{new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' }).format(new Date(`${value}-01T12:00:00`))}</option>)}</select></label>
      <label><span>Команда</span><select value={team} onChange={(event) => { setTeam(event.target.value); setEditing(null); setMessage(''); }}><option value="">Все команды</option>{data?.teams.map((item) => <option key={item.abbr} value={item.abbr}>{item.name}</option>)}</select></label>
      <label className="admin-calendar-search"><span>Поиск</span><div><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Команда или дата" /></div></label>
    </div>
    <div className="admin-calendar-summary"><span><b>{visibleItems.length}</b> матчей показано</span><span><Clock3 /><b>{filledTimes}</b> с указанным временем</span><small>Часовой пояс: МСК</small></div>
    {!data ? <Loading /> : <div className="admin-calendar-table-wrap"><table className="admin-calendar-table"><thead><tr><th>Дата</th><th>Гости</th><th>Хозяева</th><th>Время</th><th><span className="sr-only">Действия</span></th></tr></thead><tbody>{visibleItems.map((item) => <tr key={item.id}><td><time dateTime={item.date}>{adminCalendarDate(item.date)}</time></td><td><span className="admin-calendar-team"><AdminCalendarLogo abbr={item.awayTeam} url={item.awayLogoUrl} /><span><b>{item.awayTeam}</b><small>{item.awayName}</small></span></span></td><td><span className="admin-calendar-team"><AdminCalendarLogo abbr={item.homeTeam} url={item.homeLogoUrl} /><span><b>{item.homeTeam}</b><small>{item.homeName}</small></span></span></td><td>{item.startTime ? <strong className="admin-calendar-time is-set">{item.startTime} МСК</strong> : <span className="admin-calendar-time">Не указано</span>}</td><td><button type="button" className="admin-calendar-edit" onClick={() => { setEditing({ ...item }); setError(''); setMessage(''); }} aria-label={`Указать время матча ${item.awayTeam} — ${item.homeTeam}`}><Pencil /></button></td></tr>)}</tbody></table>{!visibleItems.length && <EmptyState title="Матчи не найдены" text="Измените месяц, команду или строку поиска." />}</div>}
    {editing && <div className="drawer-backdrop"><aside className="edit-drawer admin-calendar-drawer"><button type="button" className="drawer-close" onClick={() => { setEditing(null); setError(''); }} aria-label="Закрыть редактор"><X /></button><h2>Время матча</h2><div className="admin-calendar-drawer-match"><div><AdminCalendarLogo abbr={editing.awayTeam} url={editing.awayLogoUrl} /><b>{editing.awayTeam}</b><span>{editing.awayName}</span></div><strong>@</strong><div><AdminCalendarLogo abbr={editing.homeTeam} url={editing.homeLogoUrl} /><b>{editing.homeTeam}</b><span>{editing.homeName}</span></div></div><p>{adminCalendarDate(editing.date)} · время по Москве</p><form onSubmit={saveTime}><Field label="Время начала (МСК)" type="time" value={editing.startTime || ''} onChange={(value) => setEditing((current) => ({ ...current, startTime: value }))} required={false} step="60" />{error && <div className="form-error" role="alert">{error}</div>}<div className="admin-calendar-drawer-actions"><button type="submit" className="button" disabled={saving}>{saving ? 'Сохраняем…' : 'Сохранить время'}</button><button type="button" className="button button-outline dark" disabled={saving || !editing.startTime} onClick={() => setEditing((current) => ({ ...current, startTime: '' }))}>Очистить поле</button></div></form></aside></div>}
  </AdminPageHead>;
}

const emptyManagedUser = { displayName: '', email: '', password: '', role: 'editor', subscription: 'free', status: 'active' };
const managedUserRoleLabels = { user: 'Пользователь', editor: 'Редактор', admin: 'Администратор' };

function AdminUsers() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      setData(await api('/admin/users'));
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  useEffect(() => { load(); }, []);

  function createUser() {
    setError('');
    setMessage('');
    setEditing({ ...emptyManagedUser });
  }

  function editUser(user) {
    setError('');
    setMessage('');
    setEditing({ ...user, password: '' });
  }

  async function saveUser(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const method = editing.id ? 'PUT' : 'POST';
      const path = editing.id ? `/admin/users/${editing.id}` : '/admin/users';
      await api(path, { method, body: JSON.stringify(editing) });
      setMessage(editing.id ? 'Данные пользователя сохранены' : 'Пользователь добавлен');
      setEditing(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  const normalizedQuery = query.trim().toLowerCase();
  const visibleUsers = data?.users.filter((user) => {
    const matchesQuery = !normalizedQuery || `${user.displayName} ${user.email}`.toLowerCase().includes(normalizedQuery);
    return matchesQuery && (!roleFilter || user.role === roleFilter) && (!statusFilter || user.status === statusFilter);
  }) || [];

  return <AdminPageHead
    title="Пользователи"
    text="Аккаунты редакции и архив прежних пользователей. Регистрация читателей закрыта."
    action={<button type="button" className="button admin-users-add" onClick={createUser}><Plus />Добавить сотрудника</button>}
  >
    <div className="admin-users-feedback" aria-live="polite">
      {error && <div className="admin-inline-error" role="alert">{error}</div>}
      {message && <div className="admin-users-success"><CheckCircle2 />{message}</div>}
    </div>

    <section className="admin-users-section" aria-labelledby="all-users-heading">
      <div className="admin-users-section-head"><div><h2 id="all-users-heading">Все пользователи</h2><p>{data ? `${visibleUsers.length} из ${data.users.length}` : 'Загрузка списка'}</p></div></div>
      <div className="admin-users-toolbar">
        <label className="admin-users-search"><Search /><span className="sr-only">Найти пользователя</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Имя или email" /></label>
        <label><span className="sr-only">Фильтр по роли</span><select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}><option value="">Все роли</option><option value="user">Пользователь</option><option value="editor">Редактор</option><option value="admin">Администратор</option></select></label>
        <label><span className="sr-only">Фильтр по статусу</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="">Все статусы</option><option value="active">Активные</option><option value="blocked">Заблокированные</option></select></label>
      </div>
      {!data ? <Loading /> : <div className="admin-table-wrap admin-users-table-wrap"><table className="admin-table admin-users-table">
        <thead><tr><th>Пользователь</th><th>Статус</th><th>Роль</th><th>Создан</th><th>Действия</th></tr></thead>
        <tbody>{visibleUsers.map((user) => <tr key={user.id}>
          <td data-label="Пользователь"><b>{user.displayName}{user.isCurrent && <span className="admin-users-self">Вы</span>}</b><small>{user.email}</small></td>
          <td data-label="Статус"><span className={`admin-user-status ${user.status}`}>{user.status === 'blocked' ? 'Заблокирован' : 'Активен'}</span></td>
          <td data-label="Роль">{managedUserRoleLabels[user.role] || user.role}</td>
          <td data-label="Создан">{formatDate(user.createdAt)}</td>
          <td data-label="Действия"><button type="button" className="admin-user-edit" onClick={() => editUser(user)} aria-label={`Редактировать ${user.displayName}`}><Pencil /><span>Редактировать</span></button></td>
        </tr>)}</tbody>
      </table>{!visibleUsers.length && <div className="admin-users-no-results"><Search /><b>Пользователи не найдены</b><span>Измените поиск или фильтры.</span></div>}</div>}
    </section>

    {editing && <AdminUserDrawer user={editing} setUser={setEditing} saving={saving} error={error} onClose={() => { setEditing(null); setError(''); }} onSave={saveUser} />}
  </AdminPageHead>;
}

function AdminUserDrawer({ user, setUser, saving, error, onClose, onSave }) {
  const firstFieldRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    const previousFocus = document.activeElement;
    firstFieldRef.current?.focus();
    const onKeyDown = (event) => { if (event.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => { window.removeEventListener('keydown', onKeyDown); previousFocus?.focus?.(); };
  }, []);
  const isExisting = Boolean(user.id);
  return <div className="admin-user-drawer-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <aside className="admin-user-drawer" role="dialog" aria-modal="true" aria-labelledby="admin-user-drawer-title">
      <div className="admin-user-drawer-title"><div><span>{isExisting ? 'Карточка аккаунта' : 'Новый аккаунт'}</span><h2 id="admin-user-drawer-title">{isExisting ? 'Редактирование пользователя' : 'Добавление пользователя'}</h2></div><button type="button" onClick={onClose} disabled={saving} aria-label="Закрыть редактор"><X /></button></div>
      <form onSubmit={onSave}>
        {user.isCurrent && <div className="admin-user-current-note"><ShieldCheck /><span><b>Это ваш аккаунт</b><small>Роль администратора и активный статус защищены от случайного изменения.</small></span></div>}
        <label className="admin-user-field"><span>Имя <em>*</em></span><input ref={firstFieldRef} value={user.displayName} minLength="2" maxLength="80" onChange={(event) => setUser({ ...user, displayName: event.target.value })} autoComplete="name" required /></label>
        <label className="admin-user-field"><span>Email <em>*</em></span><input type="email" value={user.email} maxLength="180" onChange={(event) => setUser({ ...user, email: event.target.value })} autoComplete="email" required /></label>
        <label className="admin-user-field"><span>{isExisting ? 'Новый временный пароль' : 'Временный пароль'} {!isExisting && <em>*</em>}</span><div className="admin-user-password"><input type={showPassword ? 'text' : 'password'} value={user.password} minLength={user.password ? 10 : undefined} maxLength="200" onChange={(event) => setUser({ ...user, password: event.target.value })} autoComplete="new-password" required={!isExisting} disabled={user.isCurrent} /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'} disabled={user.isCurrent}>{showPassword ? <EyeOff /> : <Eye />}</button></div><small>{user.isCurrent ? 'Смена пароля своего аккаунта здесь недоступна.' : isExisting ? 'Оставьте пустым, чтобы не менять. Новый пароль завершит активные сеансы.' : 'Не меньше 10 символов. Передайте пароль пользователю безопасным способом.'}</small></label>
        <div className="admin-user-field-pair">
          <label className="admin-user-field"><span>Роль <em>*</em></span><select value={user.role} onChange={(event) => setUser({ ...user, role: event.target.value })} disabled={user.isCurrent}>{isExisting && <option value="user">Архивный читатель — вход закрыт</option>}<option value="editor">Редактор</option><option value="admin">Администратор</option></select></label>
        </div>
        <label className="admin-user-field"><span>Статус аккаунта <em>*</em></span><select value={user.status} onChange={(event) => setUser({ ...user, status: event.target.value })} disabled={user.isCurrent}><option value="active">Активен — вход разрешён</option><option value="blocked">Заблокирован — вход запрещён</option></select><small>Блокировка немедленно завершает все активные сеансы пользователя.</small></label>
        {user.status === 'blocked' && !user.isCurrent && <div className="admin-user-warning"><LockKeyhole /><span><b>Вход будет заблокирован</b><small>Данные пользователя сохранятся, аккаунт можно снова активировать.</small></span></div>}
        {error && <div className="admin-inline-error" role="alert">{error}</div>}
        <div className="admin-user-drawer-actions"><button type="submit" className="primary" disabled={saving}>{saving ? 'Сохраняем…' : 'Сохранить'}</button><button type="button" onClick={onClose} disabled={saving}>Отменить</button></div>
      </form>
    </aside>
  </div>;
}

function Loading(){return <div className="loading" role="status"><span/><span/><span/><b>Загрузка</b></div>}
function ErrorState({message}){return <div className="container state-card"><X/><h1>Что-то пошло не так</h1><p>{message}</p></div>}
function EmptyState({title,text}){return <div className="empty-state"><FileText/><h2>{title}</h2>{text&&<p>{text}</p>}</div>}
function NotFound(){return <div className="container state-card"><span className="big-number">404</span><h1>Страница не найдена</h1><Link className="button" to="/">На главную</Link></div>}

export default App;
